/* eslint-disable max-lines */
import { gql } from 'graphql-request';

export const listSigsGql = gql`
  query listSigs($searchText: String!) {
    listSigs(searchText: $searchText) {
      sigId
      sig
    }
  }
`;

export const syncSigGql = gql`
  mutation syncSig(
    $sig: String!
    $sigId: UUID
    $prescriptionId: UUID!
    $hasOneTimeAssociation: Boolean
    $confirmDirectionReassignment: Boolean
    $lineItemId: UUID!
  ) {
    syncSig(
      sig: $sig
      sigId: $sigId
      prescriptionId: $prescriptionId
      hasOneTimeAssociation: $hasOneTimeAssociation
      confirmDirectionReassignment: $confirmDirectionReassignment
      lineItemId: $lineItemId
    )
  }
`;

export const listPrescriptionForReviewGql = gql`
  query listPrescriptionForReview(
    $limit: Int = 25
    $offset: Int = 0
    $filter: PrescriptionReviewFilter
    $sortBy: PrescriptionReviewSortBy
  ) {
    listPrescriptionForReview(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      result: results {
        orderId
        source
        reviewType
        fillType
        patient {
          patientId
          patientDemographics {
            firstName
            lastName
            middleName
            dob
            mpi
            gender
            originalPartner {
              partnerName
              partnerId
            }
          }
          patientContact {
            phoneNumbers {
              phoneNumber
              tags
            }
            addresses {
              address {
                line1
                line2
                city
                state
                zip
              }
              tags
            }
            emails {
              email
              tags
            }
          }
          patientIdentity {
            qs1PatientId
          }
        }
        prescription {
          drug
          rxNumber
          strength
          form
          request {
            medication {
              instructions
            }
          }
          prescriptionUUID
          prescriptionOverride {
            productDetails {
              productId
              ndc
              drugName
              strength
              manufacturer
              form
              unit
              genericDrugName
            }
            drugProfile {
              drugName
              strength
              unit
              genericDrugName
              manufacturer
              ndc
              form
            }
            sig {
              sigText
            }
          }
        }
        lineItem {
          tags {
            tagName
            expiry
            createdAt
            isSystemTag
          }
          needsByDate
          shipByDate
          requestedDate
          shippingOption
          inBatchShipping
          isBusinessAddress
          requestLineItemId
        }
        fulfillingPharmacyId
        isReshipment
        isMarkedForFillLater
        isManualReview
      }
    }
  }
`;

export const getPrescriptionReviewDataGql = gql`
  query getPrescriptionReviewData(
    $lineItemId: String!
    $prescriptionId: String!
    $reviewType: PrescriptionReviewStatus!
  ) {
    getPrescriptionReviewData(
      lineItemId: $lineItemId
      prescriptionId: $prescriptionId
      reviewType: $reviewType
    ) {
      orderId
      lineItemId
      prescriptionId
      parentLineItemQuantity
      interactionData {
        status
        reviewReason
        currentSigId
        currentSig
        existingPrescriptions {
          ...PrescriptionFields
        }
      }
      prescription {
        ...PrescriptionFields
      }
    }
  }

  fragment PrescriptionFields on PatientPrescription {
    rxNumber
    drug
    orderId
    prescriber
    prescribedDrugName
    generic
    quantityPrescribed
    dispensedQuantity
    writtenDate
    originalRx
    expirationDate
    isActive
    patientName
    prescriberPhone
    refillsRemaining
    refillsAuthorized
    sig
    drugUnit
    lastFillDate
    nextRefill
    fillType
    daw
    dateOrdered
    prescriptionUUID
    strength
    form
    documentId
    attachments {
      fileKey
      fileName
    }
    request {
      medication {
        instructions
        ndc
        prescriber {
          firstName
          middleName
          lastName
          title
          npi
          phone
          fax
        }
      }
    }
    practice {
      practiceId
      practiceName
      prescriberName
      prescriberNpi
      electronicallySignedBy
      supervisor
      prescriberDea
      orderingAgent
      phone
      fax
      line1
      line2
      city
      state
      zip
      verificationStatus
    }
    prescriptionOverride {
      prescriptionUUID
      productDetails {
        productId
        ndc
        drugName
        strength
        manufacturer
        form
        unit
        genericDrugName
      }
      drugProfile {
        drugName
        strength
        unit
        genericDrugName
        manufacturer
        ndc
        form
      }
      sig {
        sigText
      }
      prescriptionDetails {
        prescriberName
        prescriberNpi
        providerId
        refills
        quantity
      }
      patientId
    }
    originatingPharmacy {
      ...PharmacyFields
    }
    transferPharmacies {
      ...PharmacyFields
    }
    providerId
    directions
    currentFillQuantity
    originalPrescriber {
      name
      firstName
      middleName
      lastName
      npi
      dea
      phoneNumber
      address {
        line1
        line2
        city
        state
        zip
      }
      title
      verificationStatus
    }
    overriddenPrescriber {
      name
      firstName
      middleName
      lastName
      npi
      dea
      phoneNumber
      address {
        line1
        line2
        city
        state
        zip
      }
      title
      verificationStatus
    }
    attestation {
      partnerId
      partnerName
      proxyPartnerId
      proxyPartnerName
      attestationId
    }
    partnerName
    partnerUuid
  }

  fragment PharmacyFields on Pharmacy {
    pharmacyAddress {
      addressLine1
      addressLine2
      addressCity
      addressState
      addressZip
    }
    phoneNumber
    dea
    name
    npi
    pharmacistName
    transferDate
  }
`;

export const resolveDuplicatePrescriptionGql = gql`
  mutation resolveDuplicatePrescriptionReview(
    $prescriptionId: UUID!
    $orderId: String!
    $isDuplicate: Boolean!
    $lineItemId: UUID!
  ) {
    resolveDuplicatePrescriptionReview(
      prescriptionId: $prescriptionId
      orderId: $orderId
      isDuplicate: $isDuplicate
      lineItemId: $lineItemId
    )
  }
`;

export const resolveShipLaterReviewGql = gql`
  mutation resolveShipLaterReview(
    $orderId: String!
    $lineItemId: UUID!
    $patientId: UUID!
    $targetState: ShipLaterReviewResolveTarget!
  ) {
    resolveShipLaterReview(
      orderId: $orderId
      lineItemId: $lineItemId
      patientId: $patientId
      targetState: $targetState
    )
  }
`;

export const resolveDrugProfileGql = gql`
  mutation resolveDrugProfile(
    $input: UpsertDrugProfileInput!
    $prescriptionId: UUID!
    $lineItemId: UUID!
  ) {
    resolveDrugProfile(input: $input, prescriptionId: $prescriptionId, lineItemId: $lineItemId)
  }
`;

// POM-5705: associate a catalog product to a prescription in drug review (inventory-module path).
export const associateCatalogProductGql = gql`
  mutation associateCatalogProduct(
    $prescriptionId: UUID!
    $lineItemId: UUID!
    $productId: String!
    $hasOneTimeAssociation: Boolean
  ) {
    associateCatalogProduct(
      prescriptionId: $prescriptionId
      lineItemId: $lineItemId
      productId: $productId
      hasOneTimeAssociation: $hasOneTimeAssociation
    )
  }
`;

export const overridePrescriptionGql = gql`
  mutation overridePrescription(
    $input: OverridePrescriptionPayloadInput
    $fromWorkQueue: Boolean
    $isPrescriptionEdit: Boolean
    $isFillEdit: Boolean
  ) {
    overridePrescription(
      input: $input
      fromWorkQueue: $fromWorkQueue
      isPrescriptionEdit: $isPrescriptionEdit
      isFillEdit: $isFillEdit
    ) {
      prescriptionUUID
      productDetails {
        productId
        ndc
        drugName
        strength
        manufacturer
        form
        unit
        genericDrugName
      }
      drugProfile {
        drugName
        strength
        unit
        genericDrugName
        manufacturer
        ndc
        form
      }
      sig {
        sigText
      }
      prescriptionDetails {
        prescriberName
        refills
        quantity
      }
      patientId
    }
  }
`;

export const getPrescriptionOverrideAuditsGql = gql`
  query listPrescriptionOverrideAudits($prescriptionUUID: UUID!) {
    listPrescriptionOverrideAudits(prescriptionUUID: $prescriptionUUID) {
      drugProfile {
        ...CommonFields
      }
      sig {
        ...CommonFields
      }
      prescriptionDetails {
        prescriberName {
          ...CommonFields
        }
        refills {
          ...CommonFields
        }
        quantity {
          ...CommonFields
        }
      }
    }
  }

  fragment CommonFields on FieldEditHistory {
    editedTimestamp
    editedDetails {
      fieldName
    }
    user {
      userId
      firstName
      lastName
    }
  }
`;

export const multiRxLineItemGql = gql`
  mutation multiRxLineItem(
    $originalLineItemId: UUID!
    $originalPrescriptionId: UUID!
    $multiOrderItemInput: [MultiOrderItemInput!]!
    $patientId: UUID!
  ) {
    multiRxLineItem(
      originalLineItemId: $originalLineItemId
      originalPrescriptionId: $originalPrescriptionId
      multiOrderItemInput: $multiOrderItemInput
      patientId: $patientId
    )
  }
`;

export const getPrescriptionSignedUrlGql = gql`
  query getPrescriptionSignedUrl($prescriptionUUID: UUID!, $fileKeys: [String!]!) {
    getPrescriptionSignedURL(prescriptionUUID: $prescriptionUUID, fileKeys: $fileKeys) {
      fileKey
      signedURL
    }
  }
`;

export const getPrescriptionFillHistoryGql = gql`
  query getPrescriptionFillHistory($prescriptionId: UUID!) {
    getPrescriptionFillHistory(prescriptionId: $prescriptionId) {
      count
      results {
        fillHistoryId
        prescriptionId
        patientId
        fillDate
        quantity
        lineItemId
        fillType
        isReshipment
        ndc
        originalNdc
        createdTimestamp
        modifiedTimestamp
        orderId
        rxNumber
        isVoided
        voidedTimestamp
        isEditedPostShipment
        prescribedQuantity
        parentOrderId
        overrides {
          ndc
          quantity
        }
      }
    }
  }
`;

export const updateFillHistoryGql = gql`
  mutation updateFillHistory($input: UpdateFillHistoryInput!) {
    updateFillHistory(input: $input)
  }
`;

export const prescriptionByNdcGql = gql`
  query prescriptionByNdc($filter: PrescriptionByNdcFilter!, $offset: Int!, $limit: Int!) {
    prescriptionByNdc(filter: $filter, offset: $offset, limit: $limit) {
      count
      results {
        fillHistoryId
        trackingNumber
        patientId
        rxNumber
        fillDate
        originalNdc
        patientFirstName
        patientLastName
        partnerName
        practiceName
        quantity
        orderId
        facility
        overRides: overrides {
          ndcCode: ndc
          quantity
        }
      }
    }
  }
`;

export const updateBulkFillHistoryGql = gql`
  mutation updateBulkFillHistory($input: [BulkFillHistoryItemInput!]!) {
    updateBulkFillHistory(input: $input)
  }
`;

export const fillHistoryUpdateReportsGql = gql`
  query fillHistoryUpdateReports(
    $limit: Int!
    $offset: Int!
    $filter: FillHistoryUpdateReportFilter
    $sortBy: FillHistoryUpdateReportSortBy
  ) {
    fillHistoryUpdateReports(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      result {
        key
        lastModified
        type
      }
    }
  }
`;

export const getFillHistoryUpdateReportDownloadUrlGql = gql`
  query getFillHistoryUpdateReportDownloadUrl($fileKey: String!) {
    getFillHistoryUpdateReportDownloadUrl(fileKey: $fileKey)
  }
`;

// The live label for a line item — the Rx scan station's printed-Rx-number
// fallback uses it to learn the sheet's barcode id and printed LOT.
export const getActiveLineItemLabelGql = gql`
  query getActiveLineItemLabel($lineItemId: String!) {
    getActiveLineItemLabel(lineItemId: $lineItemId) {
      labelSId
      lotNumber
    }
  }
`;

export const getRxByScanIdGql = gql`
  query getRxByScanId($sId: String!) {
    getRxByScanId(sId: $sId) {
      patientName
      orderId
      rxNumber
      drugName
      quantity
      lineItemId
      partnerName
      itemStatus
      fulfillingPharmacyId
      lineItemStatus
      taggedForReview
      orderStatus
      tags {
        tagName
      }
      isBatchShipment
      ndc
      lotNumber
      isScanned
      strength
      productId
      mpi
    }
  }
`;
